import { Component, OnInit, OnDestroy, ViewChild, PLATFORM_ID, Inject } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Router } from '@angular/router'; // Import Router
import { Observable, Subscription } from 'rxjs'; // Import Observable and Subscription

// Firebase Messaging
import { Messaging, onMessage } from '@angular/fire/messaging';

// Import necessary modules for a standalone AppComponent
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { RouterOutlet, RouterModule } from '@angular/router';
import { MenubarModule } from 'primeng/menubar'; // For p-menubar
import { MenuModule, Menu } from 'primeng/menu'; // For p-menu
import { DividerModule } from 'primeng/divider';
import { AvatarModule } from 'primeng/avatar';
import { AuthenticationService } from './services/authentication.service';
import { HttpClientModule } from '@angular/common/http';

import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast'; // Import ToastModule
import { MessageService } from 'primeng/api'; // Import MessageService
import { AppUser } from './users/user.model';
import { ChatSessionService } from './services/chat-session.service'; // Import ChatSessionService
import { ChatMessage } from './models/chat-message.model'; // Import ChatMessage
import { NotificationListComponent } from './addons/notification/notification-list.component';
import { TaskListComponent } from './addons/task/task-list.component';
import { IncidentListComponent } from "./addons/incident/incident-list.component";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { PromptUpdateService } from './services/prompt-update.service';
import { ApprovalListComponent } from './addons/approval/approval-list.component';
import { FileViewerComponent } from './shared/components/file-viewer/file-viewer.component';
import { environment } from '../environments/environment';
import { BUILD_INFO } from '../environments/build-info';

@Component({
  selector: 'app-root', // Default selector for AppComponent
  standalone: true,
  imports: [
    HttpClientModule,
    CommonModule,
    RouterOutlet,
    RouterModule, // Required for routerLink in p-menu
    MenubarModule,
    DividerModule,
    MenuModule,
    ButtonModule,
    AvatarModule,
    ToastModule,
    TaskListComponent,
    NotificationListComponent,
    IncidentListComponent,
    ConfirmDialogModule,
    ApprovalListComponent,
    FileViewerComponent
],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'] // Points to your CSS file
})
export class AppComponent implements OnInit, OnDestroy {

  title = 'TATC SynergyHub AI Workbench'; // You can keep your app title
  readonly environmentName = environment.environmentName ?? 'DEV';
  readonly buildVersion = BUILD_INFO.version;
  readonly buildTimestamp = BUILD_INFO.builtAt;
  mainMenuItems!: MenuItem[];
  accountMenuItems!: MenuItem[];
  user$: Observable<AppUser | null>;
  adminUser: AppUser | null = null;
  private userSubscription!: Subscription;

  @ViewChild('accountMenu') accountMenu!: Menu;
  @ViewChild(TaskListComponent) taskListComponent!: TaskListComponent;
  @ViewChild(IncidentListComponent) incidentListComponent!: IncidentListComponent;
  @ViewChild(NotificationListComponent) notificationListComponent!: NotificationListComponent;


  constructor(
    private authService: AuthenticationService,
    private router: Router,
    private messaging: Messaging, // Inject Messaging
    @Inject(PLATFORM_ID) private platformId: Object,
    private messageService: MessageService, // Inject MessageService for Toast
    private chatSessionService: ChatSessionService, // Inject ChatSessionService
    private promptUpdateService: PromptUpdateService,
  ) {
    this.user$ = this.authService.getAuthState();
  }


  ngOnInit() {
    this.accountMenuItems = [
      {
        label: 'Profile',
        icon: 'pi pi-user',
        command: () => {
          const sessionId = this.getActiveSessionId();
          if (this.adminUser && sessionId !== null) {
            this.router.navigate(['/ai-studio/session', sessionId, { outlets: { canvasOutlet: ['users', 'profile', this.adminUser.id] } }]);
          }
        }
      },
      {
        label: 'Settings',
        icon: 'pi pi-cog',
        routerLink: ['/settings']
      },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => this.logout()
      }
    ];

    this.userSubscription = this.user$.subscribe(user => {
      if (user) {
        this.adminUser = user;
      } else {
        this.adminUser = null;
      }
    });

    if (isPlatformBrowser(this.platformId)) {
      // Code that uses browser-specific APIs like navigator, window, document
      this.listenForForegroundMessages();
      this.setupServiceWorkerMessageListener();
      // any other browser-only initialization
    }
  }

  private listenForForegroundMessages() {
    if (this.messaging) {
      onMessage(this.messaging, (payload) => {
        console.log('Foreground message received. ', payload);

        const notificationTitle = payload.notification?.title ?? '';
        const notificationBody = payload.notification?.body ?? '';
        const messageId = payload.data?.['messageId'];
        const notificationId = payload.data?.['notificationId'];

        if (messageId) {
          try {
            if (notificationBody && notificationBody.length > 0) {
              const chatMessage = JSON.parse(notificationBody);
              this.processReceivedChatMessage(notificationTitle, chatMessage);
            }
            else this.chatSessionService.getChatMessageFromServer(messageId as unknown as number).subscribe(chatMessage => {
              this.processReceivedChatMessage(notificationTitle, chatMessage);
            });
          } catch (e) {
            console.error('Error parsing ChatMessage from notification body:', e);
            // Show a generic toast if parsing fails but it looked like a chat message
            this.showToast(notificationTitle, 'You have a new chat message.');
          }
        } else if (notificationTitle === 'New Task Assigned') {
          this.showToast(notificationTitle, notificationBody, 'success');
          // Refresh the task and notification lists to show the new data.
          // This assumes TaskListComponent has a public loadTasks() method.
          if (this.taskListComponent) {
            this.taskListComponent.loadTasks();
          }
        } else if (notificationTitle === 'New Incident Assigned') {
          this.showToast(notificationTitle, notificationBody, 'success');
          // Refresh the task and notification lists to show the new data.
          // This assumes TaskListComponent has a public loadTasks() method.
          if (this.incidentListComponent) {
            this.incidentListComponent.loadIncidents();
          }
        } else { // It's some other type of notification
          this.showToast(notificationTitle, notificationBody);
        }
      });
    }
  }

  private processReceivedChatMessage(notificationTitle: string, chatMessage: ChatMessage | null) {
    // Add the message to the session service
    if (chatMessage && typeof chatMessage.sessionId === 'number') {
      this.chatSessionService.addReceivedChatMessageToSession(chatMessage);
    } else {
      console.error('Received chat message is invalid or missing sessionId:', chatMessage);
      return; // Don't proceed if message is invalid
    }

    // Conditional Toast Logic
    const isUserInAiStudio = this.router.url.includes('/ai-studio/session/');
    if (isUserInAiStudio) {
      const urlSegments = this.router.url.split('/');
      const activeSessionIdFromUrl = parseInt(urlSegments[urlSegments.length - 1], 10);

      if (chatMessage.sessionId !== activeSessionIdFromUrl) {
        // User is in AI Studio but not in the session of the new message
        this.showToast(notificationTitle, `Message from ${chatMessage.sender.fullName || 'User'}: ${chatMessage.origTextContent || chatMessage.textContent.substring(0, 50) + '...'}`);
      }
      // If chatMessage.sessionId === activeSessionIdFromUrl, do nothing here.
      // The message will appear in the chat window via WebSocket/real-time updates.
    } else {
      // User is not in AI Studio session view, show the toast
      this.showToast(notificationTitle, `Message from ${chatMessage.sender.fullName || 'User'}: ${chatMessage.origTextContent || chatMessage.textContent.substring(0, 50) + '...'}`);
    }
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  private showToast(summary: string, detail: string, severity: string = 'info', life: number = 10000) {
    this.messageService.add({
      severity: severity,
      summary: summary,
      detail: detail,
      life: life
    });
  }

  navigateToLogin() {
    this.router.navigate(['/login']);
  }

  private setupServiceWorkerMessageListener(): void {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('Message from service worker:', event.data);
        // Check the message type
        if (event.data && event.data.type === 'NEW_CHAT_MESSAGE') {
          const receivedChatMessage: ChatMessage = event.data.chatMessage;
          // Now you have the ChatMessage object in the main thread
          // Use your service to add it to the session data
          this.chatSessionService.addReceivedChatMessageToSession(receivedChatMessage);
          // The ChatSessionService will handle updating the BehaviorSubject,
          // which will trigger updates in components subscribed to sessions$.
        }
        // Handle other message types from the service worker if any
      });
    }
  }

  logout() {
    this.authService.signOut().subscribe(() => {
      console.log('User signed out');
      this.router.navigate(['/login']);
    }, error => {
      console.error('Sign out error', error);
    });
  }

  private getActiveSessionId(): number | null {
    const match = this.router.url.match(/\/ai-studio\/session\/([^/()]+)/);
    if (!match) {
      return null;
    }

    const parsedSessionId = Number(match[1]);
    return Number.isFinite(parsedSessionId) ? parsedSessionId : null;
  }

  addNewSession() {
    this.chatSessionService.addSession().subscribe({
      next: (newSession) => {
        if (newSession && newSession.sessionId) {
          // Navigate to the new session. Since this is AppComponent,
          // you might want to navigate to the full path.
          this.router.navigate(['/ai-studio/session', newSession.sessionId]);
        } else {
          console.error('AppComponent: Failed to create new session or session ID is missing.');
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not create new session.' });
        }
      },
      error: (err) => {
        console.error('AppComponent: Error creating new session:', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'An error occurred while creating the session.' });
      }
    });
  }
}
